<template>
  <div>
    <div class="product-carousel swiper-container" ref="swiper">
      <div class="swiper-wrapper">
        <div v-for="image in images" :key="image.id" class="swiper-slide slide">
          <img :src="image.url" class="image" @click="lightboxOpen = true" />
        </div>
      </div>
      <div class="product-carousel__pagination"></div>
    </div>
    <transition name="lightbox">
      <div
        class="lightbox"
        v-if="lightboxOpen && imagesLoaded"
        @click="lightboxOpen = false"
      >
        <div class="lightbox__container">
          <button class="lightbox__close" @click="lightboxOpen = false">
            <i class="fal fa-times" />
          </button>
          <transition name="lightbox__image" mode="out-in">
            <img
              class="lightbox__image"
              :src="images[currentSlideIndex].masterUrl"
              :key="images[currentSlideIndex].id"
            />
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Swiper from "swiper/dist/js/swiper.js";;
import { preloadImages } from "../util";

export default {
  name: "ProductCarousel",
  selector: "#vue-product-carousel",
  props: {
    images: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      imagesLoaded: false,
      lightboxOpen: false,
      currentSlideIndex: 0,
    };
  },

  watch: {
    lightboxOpen(open) {
      document.body.style.overflowY = open ? "hidden" : "";
    },
  },

  async created() {
    const images = this.images.reduce((acc, image) => {
      return [...acc, image.url, image.masterUrl];
    }, []);
    try {
      await preloadImages(images);
    } finally {
      this.imagesLoaded = true;
    }
  },

  mounted() {
    window.addEventListener("keyup", this.handleKeyUp);

    this.swiper = new Swiper(this.$refs.swiper, {
      pagination: {
        clickable: true,
        el: ".product-carousel__pagination",
        bulletClass: "bullet",
        bulletActiveClass: "bullet--active",
      },
    });

    this.swiper.on("slideChange", () => {
      this.currentSlideIndex = this.swiper.activeIndex;
    });

    this.$eventBus.$on("variant_image_id", this.goToImageById);
  },

  methods: {
    goToImageById(id) {
      const slideIndex = this.images.findIndex((image) => image.id === id);
      if (slideIndex === -1) return;
      this.swiper.slideTo(slideIndex);
    },

    handleKeyUp(evt) {
      // these should only be used if lightbox is open
      // TODO: refactor into own lightbox component
      if (!this.lightboxOpen) return;
      switch (evt.keyCode) {
        case 27:
          // user pressed escape
          this.lightboxOpen = false;
          break;
        case 37:
          // user pressed <-
          this.swiper.slidePrev();
          break;
        case 39:
          // user pressed ->
          this.swiper.slideNext();
          break;
        default:
          break;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.lightbox {
  cursor: zoom-out;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: $white;
  z-index: z-index(lightbox);
  overflow-y: scroll;

  &-enter-active,
  &-leave-active {
    transition: opacity 0.3s ease-in-out;
  }

  &-enter,
  &-leave-to {
    opacity: 0;
  }

  &__container {
    position: relative;
    @include outer-container();
    display: flex;
    min-height: 100%;
    align-items: center;
    justify-content: center;
    @include media("<=tablet") {
      padding-right: 8px;
      padding-left: 8px;
    }
  }

  &__image {
    max-width: 100%;

    &-enter-active,
    &-leave-active {
      transition: opacity 0.5s ease-in-out;
    }

    &-enter,
    &-leave-to {
      opacity: 0;
    }
  }

  &__close {
    @include button-reset();
    position: absolute;
    top: 16px;
    right: 16px;
    font-size: 32px;
  }
}

.product-carousel {
  width: 100%;
  height: 100%;
  position: relative;

  &__pagination {
    position: absolute;
    display: flex;
    flex-direction: column;
    justify-content: center;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    z-index: 3;
    pointer-events: none;

    @include media("<=tablet") {
      position: static;
      margin-top: 24px;
      flex-direction: row;
    }
  }

  ::v-deep .bullet {
    pointer-events: all;
    cursor: pointer;
    display: block;
    width: 10px;
    height: 10px;
    border-radius: 100%;
    background-color: #b0b8b6;
    transition: background-color 0.15s ease-in;
    margin-top: 8px;

    @include media("<=tablet") {
      margin-left: 8px;
    }

    &:first-of-child {
      margin: 0;
    }

    &--active {
      background-color: $teal;
    }
  }
}

.swiper-wrapper {
  @include media("<=tablet") {
    align-items: center;
  }
}

.slide {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: zoom-in;
}

.image {
  max-width: 100%;
}
</style>
